<template>
    <App title="Station Pricing Management">
	<div class="station-pricing-page">
		<!-- Header Section -->
		<div class="page-header mb-4">
			<div class="d-flex justify-content-between align-items-center">
				<div>
					<h4 class="fw-semibold mb-1">Station Pricing Management</h4>
					<p class="text-muted small mb-0">Configure battery pricing and discounts for stations</p>
				</div>
				<button 
					class="btn btn-dark btn-sm px-3" 
					@click="showAddForm = !showAddForm"
					v-if="canAdd && !showAddForm"
				>
					<i class="fas fa-plus me-2"></i>New Pricing
				</button>
			</div>
		</div>

		<!-- Add Pricing Form -->
		<transition name="fade">
			<div v-if="showAddForm" class="mb-4">
				<div class="card border-0 shadow-sm">
					<div class="card-header bg-white border-0 py-3">
						<h6 class="mb-0 fw-semibold">Add New Pricing</h6>
					</div>
					<div class="card-body">
						<PriceForm
							:url="route('price.ajax.create')"
							:stations="stations"
							:battery-types="orderTypes"
							@success="handlePricingSuccess"
							@error="handlePricingError"
							@cancel="showAddForm = false"
						/>
					</div>
				</div>
			</div>
		</transition>

		<!-- Pricing List -->
		<div class="pricing-list">
			<!--
			<div v-if="pricings.length === 0" class="text-center py-5">
				<i class="fas fa-inbox fa-3x text-muted mb-3"></i>
				<p class="text-muted">No pricing configured yet</p>
			</div>
			-->

			<div class="row g-3">

                <!-- Pricing Datatable -->
				<Datatable :options="options" ref="priceDatatable" />
				
			</div>
		</div>
	</div>
    </App>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'
import PriceForm from '@/vue/components/PriceForm.vue'
import App from '@/vue/templates/App.vue'
import Datatable from '@/vue/components/Datatable.vue'
import { CellRender, Column } from '@/vue/utils/Datatable'
import Swal from 'sweetalert2'
import { Role, hasRole } from '@/models/Admin'

interface Station {
	value: string
	name: string
}

interface orderType {
	value: string
	name: string
}

interface Pricing {
	id: string
	station: string
	stationName: string
	batteryType: string
	originalPrice: number
	finalPrice: number
	activatedAt: string

}

@Component({
	components: {
		PriceForm,
        App,
        Datatable
	}
})
class Pricing extends Vue {


	@Prop({ type: Array, default: () => [] })
	readonly stations!: Station[]

	@Prop({ type: Array, default: () => [] })
	readonly orderTypes!: orderType[]

	showAddForm = false
	pricings: Pricing[] = []

		get canAdd() {
		return hasRole(this.$user, [Role.SUPER_ADMIN, Role.COUNTRY_MANAGER])
	}


	handlePricingSuccess(event: any) {
		this.showToast(event.message, 'success')
		this.showAddForm = false
		this.handleTableRefresh()
		
	}

	handlePricingError(event: any) {
		this.showToast(event.message, 'error')
	}

	handleTableRefresh() {
		// @ts-ignore
		const datatableRef = this.$refs.priceDatatable as any
		if (datatableRef && datatableRef.datatable) {
			datatableRef.datatable.ajax.reload(null, false)
		} 
	}

	formatCurrency(amount: number): string {
		return new Intl.NumberFormat('en-NG', {
			style: 'currency',
			currency: 'NGN',
			minimumFractionDigits: 0
		}).format(amount / 100)
	}

	formatDate(date: string): string {
		return new Date(date).toLocaleDateString('en-NG', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		})
	}


	showToast(message: string, type: 'success' | 'error' ) {
        
        Swal.fire({
            toast: true,
            position: 'top-end', 
            icon: type, 
            title: message,
            showConfirmButton: false, 
            timer: 3000, 
            timerProgressBar: true,
            didOpen: (toast) => {
                // Pause the timer when the mouse hovers over the toast
                toast.addEventListener('mouseenter', Swal.stopTimer)
                // Resume the timer when the mouse leaves
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        })
    }


	get buttons() {
		let buttons: any[] = []
		buttons.push('refresh')
		
		buttons.push('trashed')

		return buttons
	}

	get columns() {
		let columns: any[] = []
		columns.push({
			data: 'id',
			title: 'ID',
		})
		columns.push({
			data: 'station.title',
			title: 'Station',
		})
		
		columns.push({
			data: 'price_label',
			title: 'Battery Type',
		})
		columns.push({
			data: 'amount',
			title: 'amount',
			render: CellRender.money(),
		})
		
		
		columns.push({
			data: 'activated_at',
			title: 'Effective Date',
			render: CellRender.date(),
			orderable: true,
		
		})

		columns.push({
			data: 'created_at',
			title: 'Registered',
			render: CellRender.date(),
			orderable: true,
			name: 'created_at',
		})

		columns.push(Column.actions(this.$gate))

		return columns
	}

	get options() {
		return {
			ajax: {
				url: this.route('price.ajax.table'),
				method: 'POST',
			},
			buttons: this.buttons,
			columns: this.columns,
		}
	}
}

export default toNative(Pricing)
</script>

<style scoped>
.station-pricing-page {
	padding: 1.5rem;
	max-width: 1400px;
	margin: 0 auto;
}

.page-header h4 {
	color: #212529;
	font-size: 1.5rem;
}

.pricing-card {
	transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pricing-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
}

.badge {
	font-weight: 500;
	padding: 0.35rem 0.65rem;
}

.bg-light {
	background-color: #f8f9fa !important;
}

.bg-success-subtle {
	background-color: #d1e7dd !important;
}

.text-success {
	color: #0f5132 !important;
}

.form-check-input:checked {
	background-color: #212529;
	border-color: #212529;
}

.form-check-input:focus {
	border-color: #212529;
	box-shadow: 0 0 0 0.25rem rgba(33, 37, 41, 0.25);
}

.discount-config .input-group-text {
	background-color: #f8f9fa;
	border-color: #dee2e6;
}

.discount-config .btn-dark {
	padding: 0.25rem 0.75rem;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
	opacity: 0;
}

.slide-down-enter-active, .slide-down-leave-active {
	transition: all 0.3s ease;
	max-height: 100px;
	overflow: hidden;
}

.slide-down-enter-from, .slide-down-leave-to {
	max-height: 0;
	opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
	.station-pricing-page {
		padding: 1rem;
	}

	.page-header h4 {
		font-size: 1.25rem;
	}
}
</style>